import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { LuGitPullRequest } from 'react-icons/lu';

import { fetchGitReviewerSetting, updateGitReviewerSetting } from '../../shared/api/user-settings';

const GitReviewerSetting: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGitReviewerSetting()
      .then((s) => !cancelled && setEnabled(s.enabled))
      .catch(() => !cancelled && setLoadError('Failed to load this setting.'))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next);
    setIsSaving(true);
    setError(null);
    try {
      const saved = await updateGitReviewerSetting(next);
      setEnabled(saved.enabled);
      toast.success(saved.enabled ? 'Git Reviewer agent enabled.' : 'Git Reviewer agent disabled.');
    } catch {
      setEnabled(!next);
      setError('Failed to update this setting.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="card bg-base-100 shadow-card">
        <div className="card-body p-4">
          <div className="skeleton h-12 w-full" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="alert alert-error shadow-card">
        <span>{loadError}</span>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-card">
      <div className="card-body p-4 gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="text-lg shrink-0 opacity-60">
              <LuGitPullRequest />
            </span>
            <div>
              <div className="font-semibold">Git Reviewer agent</div>
              <div className="text-sm opacity-70 max-w-lg">
                Watches all connected repos' PRs, leaves reviews, and runs on a 10-minute schedule.
              </div>
            </div>
          </div>
          <input
            type="checkbox"
            className="toggle toggle-primary shrink-0"
            checked={enabled}
            onChange={handleToggle}
            disabled={isSaving}
            title="Enable Git Reviewer agent"
          />
        </div>

        {error && (
          <div className="alert alert-error py-2">
            <span className="text-sm">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GitReviewerSetting;
